/**
 * Canonical two-tier section registry.
 *
 * Tier 1 — INVESTIGATIONS: authored reads with a thesis + a claims register.
 * Tier 2 — DATA SETS: the standing record, grouped by civic area, query-first.
 *
 * This is the single source of truth the home page and the ⌘K command palette
 * read from, replacing the hand-maintained `FILES` array that used to live in
 * `app/page.tsx`. It carries metadata only (names, hrefs, areas, the read↔data
 * links) — live figures and claim counts are layered on at load time in
 * `app/_lib/home.ts` so no number is ever a literal here.
 *
 * ROLLOUT: only publicly launched (indexed, in `sitemap.ts`) sections appear.
 * transit / cycling / finances are noindex direct-URL today and are
 * intentionally omitted until their go-live stories ship.
 */

export type CivicArea = "Environment" | "Money" | "City Hall" | "Services";

/**
 * Per-topic identity accent. Theme-aware tokens resolved to `var(--<token>)`
 * at the component. Most sections take a diagnostic-palette hue
 * (azure/moss/ochre/teal/cyan/pine); where that hue reads "too digital" or
 * carries the wrong civic meaning for identity use, the section takes a
 * dedicated editorial `sec-*` token instead (the chart token is left alone for
 * analytical charts). See the `--sec-*` block in globals.css.
 */
export type AccentToken =
  | "azure"
  | "moss"
  | "ochre"
  | "violet"
  | "teal"
  | "cyan"
  | "brick"
  | "slate"
  | "pine"
  | "sec-council"
  | "sec-finances"
  | "sec-engagement"
  | "sec-alerts";

export const accentVar = (token: AccentToken) => `var(--${token})`;

// ── canonical registry: ALL sections (Epic 021) ─────────────────────────
// `SECTIONS` is the single source for the section-redesign migration and the
// design-system conformance check — it lists every section, launched or not.
// The home + nav render the SURFACED subset via INVESTIGATIONS / DATA_SETS
// below; noindex sections (surfaced:false) stay off those surfaces but still
// carry their identity accent so their own pages can align with the redesign.
export type SectionSlug =
  | "water"
  | "climate"
  | "transit"
  | "cycling"
  | "council"
  | "property-taxes"
  | "finances"
  | "civic-partners"
  | "housing"
  | "engagement"
  | "calgary-plan"
  | "alerts";

export type Section = {
  slug: SectionSlug;
  title: string;
  /** Masthead topic word, e.g. "water". */
  topic: string;
  area: CivicArea;
  /** Topic identity colour (theme-aware token). */
  accent: AccentToken;
  /** Canonical entry route. */
  href: string;
  status: "live" | "soon";
  /** Shown on the home + nav. False for noindex / not-yet-launched sections. */
  surfaced: boolean;
};

export const SECTIONS: Section[] = [
  { slug: "water", title: "Water", topic: "water", area: "Environment", accent: "azure", href: "/water", status: "live", surfaced: true },
  { slug: "climate", title: "Climate", topic: "climate", area: "Environment", accent: "moss", href: "/climate", status: "live", surfaced: true },
  { slug: "property-taxes", title: "Property taxes", topic: "property-taxes", area: "Money", accent: "ochre", href: "/property-taxes/by-ward", status: "live", surfaced: true },
  { slug: "council", title: "Council", topic: "council", area: "City Hall", accent: "sec-council", href: "/council/alignment", status: "live", surfaced: true },
  { slug: "housing", title: "Housing", topic: "housing", area: "City Hall", accent: "teal", href: "/housing", status: "live", surfaced: true },
  { slug: "engagement", title: "Engagement", topic: "engagement", area: "City Hall", accent: "sec-engagement", href: "/engagement", status: "live", surfaced: false },
  { slug: "finances", title: "Finances", topic: "finances", area: "Money", accent: "sec-finances", href: "/finances", status: "live", surfaced: false },
  { slug: "civic-partners", title: "Civic partners", topic: "civic-partners", area: "Money", accent: "brick", href: "/civic-partners", status: "live", surfaced: false },
  { slug: "transit", title: "Transit", topic: "transit", area: "Services", accent: "cyan", href: "/transit", status: "live", surfaced: false },
  { slug: "cycling", title: "Cycling", topic: "cycling", area: "Services", accent: "pine", href: "/cycling", status: "live", surfaced: false },
  // The Calgary Plan = the Municipal Development Plan (the umbrella above
  // housing/transit/cycling), so it earns its own top-level identity rather
  // than nesting under housing. Accent `violet` (an unused diagnostic token;
  // council uses `sec-council`). Direct-URL only (surfaced:false) + per-route
  // noindex until a deliberate go-live story (progressive rollout).
  { slug: "calgary-plan", title: "Calgary Plan", topic: "calgary plan", area: "City Hall", accent: "violet", href: "/calgary-plan", status: "live", surfaced: false },
  // /alerts is the one entry here that is not a topic — it is a service
  // (spec 026, proximity alerts): tell us your address, we email you when a
  // land-use application shows up near it. It lives in the registry because
  // `check-design-system.mjs` requires every top-level route segment under
  // app/ to be registered, and because a service page still needs an identity
  // accent to compose a hero from. Accent `sec-alerts` (civic indigo) rather
  // than a diagnostic hue: the chart tokens carry analytical meaning, and
  // `slate` — the only unused one — is literally the low-attention colour,
  // which is the wrong thing to say about an alert.
  // Direct-URL only (surfaced:false) + per-route noindex from
  // app/alerts/layout.tsx until the go-live story (sc-29535, FR-037a).
  { slug: "alerts", title: "Proximity alerts", topic: "alerts", area: "City Hall", accent: "sec-alerts", href: "/alerts", status: "live", surfaced: false },
];

/** Identity accent for a section, as a `var(--token)` string. */
export const sectionAccent = (slug: SectionSlug): string =>
  accentVar(SECTIONS.find((s) => s.slug === slug)?.accent ?? "azure");

/**
 * "The record" hue — one teal-green, constant across every section.
 *
 * Pair to `sectionAccent()`: a section's identity accent is "the read" (the
 * argued, cited investigation), and this is "the record" (the standing data).
 * The read hue is per-section; the record hue is global, so the read↔record
 * contrast stays legible ACROSS sections (per-section record hues would just
 * read as more section colour and dissolve the metaphor). Resolved token lives
 * in `globals.css` (`--record`, light + dark). Used as a stroke / dot accent on
 * record-tagged surfaces — never as a saturated fill.
 */
export const RECORD_ACCENT = "var(--record)";

/** A section's read↔record identity pair, both as `var(--token)` strings. */
export type SectionRoles = {
  /** Per-section identity hue — the argued, cited investigation. */
  read: string;
  /** Global teal-green — the standing data record. */
  record: string;
};

/**
 * Read↔record identity pair for a section. Pass `read` to investigation chrome
 * (lead story, the figpanel headline, "read the claims" CTA) and `record` to
 * anything tagged data/the record (the live tracker card, data figure cards,
 * footer record pips, the legend's record dot).
 */
export const sectionRoles = (slug: SectionSlug): SectionRoles => ({
  read: sectionAccent(slug),
  record: RECORD_ACCENT,
});

export type Investigation = {
  id: string;
  name: string;
  area: CivicArea;
  /** Topic identity colour, used on its figure + sparkline. */
  accent: AccentToken;
  /** Short headline question — the thesis, plainly stated. */
  question: string;
  /** The read (the argued, cited investigation). */
  href: string;
  /** The numbers (the standing record this read draws on). */
  dataHref: string;
  /** Data-set id this investigation draws on (the read↔record link). */
  drawsOn: string;
  /** The one investigation that leads the front page. Exactly one. */
  featured?: boolean;
};

export type DataSet = {
  id: string;
  name: string;
  area: CivicArea;
  href: string;
  blurb: string;
  /** True when an investigation is built on this set (renders the read pip). */
  hasInvestigation?: boolean;
};

// Order here IS the editorial order on the home page. `featured` leads; the
// rest run in "In the news" in array order. Retarget the lead by moving the
// `featured` flag — no other change needed.
export const INVESTIGATIONS: Investigation[] = [
  {
    id: "housing",
    name: "Housing",
    area: "City Hall",
    accent: "ochre",
    question: "Did rezoning enable the units it promised?",
    href: "/housing",
    dataHref: "/housing/cost",
    drawsOn: "d-development",
    featured: true,
  },
  {
    id: "climate",
    name: "Climate",
    area: "Environment",
    accent: "moss",
    question: "Did the climate emergency deliver what it directed?",
    href: "/climate",
    dataHref: "/climate/emissions",
    drawsOn: "d-emissions",
  },
  {
    id: "water",
    name: "Water",
    area: "Environment",
    accent: "azure",
    question: "Is Calgary's water use sustainable as the city grows?",
    href: "/water",
    dataHref: "/water/demand",
    drawsOn: "d-water",
  },
];

export const DATA_SETS: DataSet[] = [
  {
    id: "d-water",
    name: "Water system",
    area: "Environment",
    href: "/water/demand",
    blurb: "Demand, the daily tracker, the river ceiling.",
    hasInvestigation: true,
  },
  {
    id: "d-emissions",
    name: "Emissions",
    area: "Environment",
    href: "/climate/emissions",
    blurb: "Community and corporate GHG against the target.",
    hasInvestigation: true,
  },
  {
    id: "d-taxes",
    name: "Property taxes",
    area: "Money",
    href: "/property-taxes/by-ward",
    blurb: "The assessment roll, by ward and community.",
  },
  {
    id: "d-council",
    name: "Council records",
    area: "City Hall",
    href: "/council/alignment",
    blurb: "Votes, alignment, public hearings.",
  },
  {
    id: "d-development",
    name: "Housing & rezoning",
    area: "City Hall",
    href: "/housing",
    blurb: "Units enabled and the cost of a permit.",
    hasInvestigation: true,
  },
];

// Only areas that actually carry a launched data set, in display order.
export const CIVIC_AREAS: CivicArea[] = ["Environment", "Money", "City Hall"];

// Identity colour per civic area, used on the "Browse by topic" headers.
export const AREA_ACCENT: Record<CivicArea, AccentToken> = {
  Environment: "moss",
  Money: "ochre",
  "City Hall": "violet",
  Services: "cyan",
};

export const FEATURED_INVESTIGATION =
  INVESTIGATIONS.find((i) => i.featured) ?? INVESTIGATIONS[0];

export const RUNNING_INVESTIGATIONS = INVESTIGATIONS.filter(
  (i) => i.id !== FEATURED_INVESTIGATION.id,
);

/** Flat, serializable index for the ⌘K command palette. */
export type SearchItem = {
  kind: "read" | "data";
  name: string;
  desc: string;
  href: string;
  area: string;
};

export const SEARCH_INDEX: SearchItem[] = [
  ...INVESTIGATIONS.map((i): SearchItem => ({
    kind: "read",
    name: i.name,
    desc: i.question,
    href: i.href,
    area: "Investigation",
  })),
  ...DATA_SETS.map((d): SearchItem => ({
    kind: "data",
    name: d.name,
    desc: d.blurb,
    href: d.href,
    area: d.area,
  })),
];
